import {
  MilesProgram,
  calculateDynamicRate,
  calculateTotalPayout,
} from "@/lib/miles-calculations";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { TransactionTypes } from "./user-credentials";

export enum WarnTypes {
  OVER = "over",
  UNDER = "under",
  NONE = "none",
}

export enum ErrorTypes {
  ERROR = "error",
  SECURE_QUOTE_ERROR = "secure_quote_error",
  NONE = "none",
}

export interface ErrorDetails {
  message?: string;
  status?: number;
  code?: string;
  timestamp?: Date;
}

export interface TotalPayout {
  baseAmount: number;
  statusBonusAmount: number;
  totalPayout: number;
}

interface DashboardStore {
  warnState: WarnTypes;
  setWarnState: (warnType: WarnTypes) => void;
  clearWarnState: () => void;

  errorState: ErrorTypes;
  errorDetails: ErrorDetails | null;
  setErrorState: (errorType: ErrorTypes, details?: ErrorDetails) => void;
  clearErrorState: () => void;

  totalSanitizedMiles: number;
  setTotalSanitizedMiles: (miles: number) => void;

  program: MilesProgram | null;
  setProgram: (program: MilesProgram) => void;
  updateProgram: (updates: Partial<MilesProgram>) => void;
  //programs: MilesProgram[];
  //addProgram: (program: MilesProgram) => void;
  //updateProgram: (programId: string, updates: Partial<MilesProgram>) => void;
  //removeProgram: (programId: string) => void;
  //removeAllPrograms: () => void;

  setAverageRate: (averageRate: number) => void;
  averageRate: number;

  setSanitizedMiles: ({
    miles,
    transactionType,
  }: {
    miles: number;
    transactionType: TransactionTypes;
  }) => void;

  totalPayout: TotalPayout;
  setTotalPayout: (totalPayout: TotalPayout) => void;

  isCreatingNewQuote: boolean;
  setIsCreatingNewQuote: (isCreatingNewQuote: boolean) => void;
  isSecuringQuote: boolean;
  setIsSecuringQuote: (value: boolean) => void;
}

export const useDashboardStore = create<DashboardStore>()(
  persist(
    (set) => ({
      isCreatingNewQuote: false,
      setIsCreatingNewQuote: (isCreatingNewQuote: boolean) =>
        set(() => ({ isCreatingNewQuote })),

      isSecuringQuote: false,
      setIsSecuringQuote: (value: boolean) => set({ isSecuringQuote: value }),

      errorState: ErrorTypes.NONE,
      errorDetails: null,
      setErrorState: (errorType, details) =>
        set(() => ({
          errorState: errorType,
          errorDetails: details || null,
        })),
      clearErrorState: () =>
        set(() => ({
          errorState: ErrorTypes.NONE,
          errorDetails: null,
        })),

      warnState: WarnTypes.NONE,
      setWarnState: (warnType) => set(() => ({ warnState: warnType })),
      clearWarnState: () => set(() => ({ warnState: WarnTypes.NONE })),

      totalSanitizedMiles: 0,
      setTotalSanitizedMiles: (miles: number) =>
        set(() => ({ totalSanitizedMiles: miles })),

      program: null,
      setProgram: (program: MilesProgram) => set(() => ({ program })),
      updateProgram: (updates: Partial<MilesProgram>) =>
        set((state) => ({
          program: state.program ? { ...state.program, ...updates } : null,
        })),

      averageRate: 0,
      setAverageRate: (averageRate) =>
        set(() => ({ averageRate: averageRate })),
      setSanitizedMiles: ({
        miles,
        transactionType,
      }: {
        miles: number;
        transactionType: TransactionTypes;
      }) =>
        set((state) => {
          if (!state.program) return state;

          const dynamicRate = calculateDynamicRate({
            miles,
            transactionType,
            sellPrices: state.program.sellPrices,
            buyPrices: state.program.buyPrices,
            fallbackRate: state.program.ratePerMile,
          });
          const { statusBonusAmount, totalPayout } = calculateTotalPayout({
            totalMiles: miles,
            dynamicRate,
            loyaltyStatus: state.program.loyaltyStatus,
            transactionType,
            promoCode: state.program.promoCode,
          });

          return {
            ...state,
            program: {
              ...state.program,
              sanitizedMiles: miles,
              ratePerMile: dynamicRate,
              statusBonus: statusBonusAmount,
              totalPayout,
            },
          };
        }),

      totalPayout: {
        baseAmount: 0,
        statusBonusAmount: 0,
        totalPayout: 0,
      },
      setTotalPayout: (totalPayout) =>
        set(() => ({ totalPayout: totalPayout })),
    }),
    {
      name: "dashboard-state",
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
);
